import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { ReportSdk } from 'lib-klab-sdks';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';

import { ConnectivityReportsPort } from '../../../app/ports/reports/connectivity-reports.port';
import { AppEventsService } from '../../events/products-events.service';
import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';

import { ConnectivityReportRequest } from '../../../domain/models/reports/request/connectivity-report.request';
import { ConnectivityReportResponse } from '../../../domain/models/reports/connectivity-report.model';

import { mapSdkConnectivityResponseToDomain } from '../../mappers/reports/connectivity-reports.mapper';
import { connectivityReportsSdkMock } from '../../mocks/report/connectivity-reports.mock';

type ConnectivityReportsMockAction = 'getConnectivityReport';

@Injectable({ providedIn: 'root' })
export class ConnectivityReportsPortAdapter implements ConnectivityReportsPort {
    constructor(
        private connectivityReportsService: ReportSdk.ConnectivityReportsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getConnectivityReport(payload: ConnectivityReportRequest): Observable<ConnectivityReportResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-connectivity');

        const sdkPayload: ReportSdk.ConnectivityRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
        };

        const source$: Observable<ReportSdk.ConnectivityResponse> = this.shouldMock('getConnectivityReport')
            ? of(connectivityReportsSdkMock.getConnectivityReport(sdkPayload.employeeId, sdkPayload.dateRange)).pipe(delay(360))
            : this.connectivityReportsService.getConnectivityReport(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkConnectivityResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-connectivity')),
        );
    }

    private shouldMock(action: ConnectivityReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.connectivityReports?.[action]);
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
